import { orderBy } from 'lodash';
import { Injectable } from '@angular/core';
import { map, switchMap } from 'rxjs/operators';
import { FetchPolicy } from '@apollo/client/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { addHours, addWeeks, endOfDay, isAfter, isBefore, startOfDay, subMonths } from 'date-fns';

import { IPagination } from '@diktup/frontend/models';
import {
  CustomTeamType,
  CustomFixtureType,
  FixtureLeagueType,
  CurrentUserRankType,
  CustomLeagueTypeEnum,
  EventSubscriptionType,
  FixtureStatusTypeEnum,
  GetCurrentUserRankGQL,
  AddUserToEventSubscriptionGQL,
  LeaderboardWithRequiredCycleInput,
  FindCustomTeamByTargetPaginatedGQL,
  RemoveUserFromEventSubscriptionGQL,
  FindCustomFixtureByTargetPaginatedGQL,
  FindCustomLeagueByTargetPaginatedGQL,
  CustomLeagueType,
} from '@sifca-monorepo/widget-generator';
import {
  PlaceBetGQL,
  FixtureType,
  BetOrderType,
  BetOrderInput,
  OddsWithBetsType,
  FixtureFilterInput,
  FixtureWithOddsType,
  FindOneByFixtureIdGQL,
  OddsBookmakersBetsType,
  OddsBookmakersBetsValuesType,
  FavoriteLeagueDefinitionType,
  GetPreMatchOddsFromProviderGQL,
  GetFixturesWithFilterAndOddsGQL,
  GetFixturesWithFilterWithSubsGQL,
  GetFavoriteLeagueDefinitionsPaginationGQL,
  GetCurrentUserPendingBetOrdersPaginationGQL,
  GetCurrentUserFinishedBetOrdersPaginationGQL,
} from '@sifca-monorepo/sport-generator';

export interface FixtureWithExtras extends FixtureWithOddsType {
  finished?: boolean;
  isLive?: boolean;
  soon?: boolean;
}

export interface FixtureWithMatchExtras extends FixtureType {
  finished?: boolean;
  isLive?: boolean;
  hasScore?: boolean;
  homeScore?: number;
  awayScore?: number;
}

export interface CustomFixtureWithExtras extends CustomFixtureType {
  finished?: boolean;
  isLive?: boolean;
  hasScore?: boolean;
  homeScore?: number | string;
  awayScore?: number | string;
}
export interface GroupedLeague {
  leagueId: number;
  league: FixtureLeagueType;
  allMatches: FixtureWithExtras[];
  filteredMatches: FixtureWithExtras[];
  matches?: FixtureWithExtras[];
}

@Injectable({
  providedIn: 'root',
})
export class MobileMatchesService {
  private isLast: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private matchOdds: BehaviorSubject<any[]> = new BehaviorSubject(null);
  private filteredGroupedLeagues = new BehaviorSubject<GroupedLeague[]>([]);
  private betSlips: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  private loadingOrders: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private loadingMatches: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private isBetModalOpen: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private fixture: BehaviorSubject<FixtureWithExtras> = new BehaviorSubject(null);
  private customTeams: BehaviorSubject<CustomTeamType[]> = new BehaviorSubject([]);
  private customPagination: BehaviorSubject<IPagination> = new BehaviorSubject(null);
  private loadingCustomMatches: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private customLeagues: BehaviorSubject<CustomLeagueType[]> = new BehaviorSubject([]);
  private matchs: BehaviorSubject<FixtureWithMatchExtras[]> = new BehaviorSubject(null);
  private todayMatchs: BehaviorSubject<FixtureWithOddsType[]> = new BehaviorSubject(null);
  private loadingMoreCustomMatches: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private playedMatchsPagination: BehaviorSubject<IPagination> = new BehaviorSubject(null);
  private upComingMatchs: BehaviorSubject<FixtureWithOddsType[]> = new BehaviorSubject(null);
  private customMatches: BehaviorSubject<CustomFixtureWithExtras[]> = new BehaviorSubject([]);
  private betOrders: BehaviorSubject<BetOrderType[]> = new BehaviorSubject<BetOrderType[]>([]);
  private nearestMatches: BehaviorSubject<FixtureWithMatchExtras[]> = new BehaviorSubject(null);
  private favouriteLeagues: BehaviorSubject<FavoriteLeagueDefinitionType[]> = new BehaviorSubject(null);
  private userRank: BehaviorSubject<CurrentUserRankType> = new BehaviorSubject<CurrentUserRankType>(null);
  private selectedFixture: BehaviorSubject<FixtureWithExtras> = new BehaviorSubject<FixtureWithExtras>(null);
  private selectedOdd: BehaviorSubject<OddsBookmakersBetsType> = new BehaviorSubject<OddsBookmakersBetsType>(null);
  private selectedBet: BehaviorSubject<OddsBookmakersBetsValuesType> = new BehaviorSubject<OddsBookmakersBetsValuesType>(null);

  pageIndex = 0;
  pageLimit = 10;
  customPageIndex = 0;
  playedMatchsPage = 0;
  upcomingPageIndex = 0;
  upcomingPageLimit = 40;
  favMatchsExist: boolean;
  matchesPageLimit: number = 20;
  favoriteLeagues: FavoriteLeagueDefinitionType[];
  oddsLoadingMap: { [fixtureId: number]: boolean } = {};

  get customLeagues$(): Observable<CustomLeagueType[]> {
    return this.customLeagues.asObservable();
  }

  get nearestMatches$(): Observable<FixtureWithMatchExtras[]> {
    return this.nearestMatches.asObservable();
  }

  get customTeams$(): Observable<CustomTeamType[]> {
    return this.customTeams.asObservable();
  }
  get customMatches$(): Observable<CustomFixtureWithExtras[]> {
    return this.customMatches.asObservable();
  }

  get customPagination$(): Observable<IPagination> {
    return this.customPagination.asObservable();
  }

  get loadingCustomMatches$(): Observable<boolean> {
    return this.loadingCustomMatches.asObservable();
  }

  get loadingMoreCustomMatches$(): Observable<boolean> {
    return this.loadingMoreCustomMatches.asObservable();
  }

  get matchs$(): Observable<FixtureWithMatchExtras[]> {
    return this.matchs.asObservable();
  }

  get isLast$(): Observable<boolean> {
    return this.isLast.asObservable();
  }

  get userRank$(): Observable<CurrentUserRankType> {
    return this.userRank.asObservable();
  }

  get loadingOrders$(): Observable<boolean> {
    return this.loadingOrders.asObservable();
  }

  get betOrders$(): Observable<BetOrderType[]> {
    return this.betOrders.asObservable();
  }
  set betOrders$(value: any) {
    this.betOrders.next(value);
  }
  // Selected observables + helpers
  get selectedOdd$(): Observable<OddsBookmakersBetsType> {
    return this.selectedOdd.asObservable();
  }
  set selectedOdd$(value: any) {
    this.selectedOdd.next(value);
  }

  get isBetModalOpen$(): Observable<boolean> {
    return this.isBetModalOpen.asObservable();
  }
  set isBetModalOpen$(value: any) {
    this.isBetModalOpen.next(value);
  }

  get selectedBet$(): Observable<OddsBookmakersBetsValuesType> {
    return this.selectedBet.asObservable();
  }
  set selectedBet$(value: any) {
    this.selectedBet.next(value);
  }

  get selectedFixture$(): Observable<FixtureWithExtras> {
    return this.selectedFixture.asObservable();
  }
  set selectedFixture$(value: any) {
    this.selectedFixture.next(value);
  }

  get fixture$(): Observable<FixtureWithExtras> {
    return this.fixture.asObservable();
  }

  get todayMatchs$(): Observable<FixtureWithOddsType[]> {
    return this.todayMatchs.asObservable();
  }

  get playedMatchsPagination$(): Observable<IPagination> {
    return this.playedMatchsPagination.asObservable();
  }

  get favouriteLeagues$(): Observable<FavoriteLeagueDefinitionType[]> {
    return this.favouriteLeagues.asObservable();
  }

  get betSlips$(): Observable<any[]> {
    return this.betSlips.asObservable();
  }

  set betSlips$(value: any) {
    this.betSlips.next(value);
  }

  get loadingMatches$(): Observable<boolean> {
    return this.loadingMatches.asObservable();
  }

  get matchOdds$(): Observable<any[]> {
    return this.matchOdds.asObservable();
  }

  get filteredGroupedLeagues$(): Observable<any[]> {
    return this.filteredGroupedLeagues.asObservable();
  }
  set filteredGroupedLeagues$(value: any) {
    this.filteredGroupedLeagues.next(value);
  }

  get upComingMatchs$(): Observable<FixtureWithOddsType[]> {
    return this.upComingMatchs.asObservable();
  }
  set upComingMatchs$(value: any) {
    this.upComingMatchs.next(value);
  }

  searchString: string = '';
  sports: CustomLeagueTypeEnum[] = [CustomLeagueTypeEnum.FOOTBALL];
  status: FixtureStatusTypeEnum[] = [];

  constructor(
    private placeBetGQL: PlaceBetGQL,
    private GetCurrentUserRankGQL: GetCurrentUserRankGQL,
    private findOneByFixtureIdGQL: FindOneByFixtureIdGQL,
    private addUserToEventSubscriptionGQL: AddUserToEventSubscriptionGQL,
    private getPreMatchOddsFromProviderGQL: GetPreMatchOddsFromProviderGQL,
    private getFixturesWithFilterAndOddsGQL: GetFixturesWithFilterAndOddsGQL,
    private getFixturesWithFilterWithSubsGQL: GetFixturesWithFilterWithSubsGQL,
    private removeUserFromEventSubscriptionGQL: RemoveUserFromEventSubscriptionGQL,
    private findCustomTeamByTargetPaginatedGQL: FindCustomTeamByTargetPaginatedGQL,
    private findCustomLeagueByTargetPaginatedGQL: FindCustomLeagueByTargetPaginatedGQL,
    private findCustomFixtureByTargetPaginatedGQL: FindCustomFixtureByTargetPaginatedGQL,
    private getFavoriteLeagueDefinitionsPaginationGQL: GetFavoriteLeagueDefinitionsPaginationGQL,
    private getCurrentUserPendingBetOrdersPaginationGQL: GetCurrentUserPendingBetOrdersPaginationGQL,
    private getCurrentUserFinishedBetOrdersPaginationGQL: GetCurrentUserFinishedBetOrdersPaginationGQL,
  ) {}

  findCustomFixtureByTargetPaginated(reset = false, leagues?: string[]): Observable<CustomFixtureWithExtras[]> {
    if (reset) {
      this.customPageIndex = 0;
      this.customMatches.next([]);
    }

    const isLoadingMore = this.customPageIndex > 0;
    this.loadingCustomMatches.next(!isLoadingMore);
    this.loadingMoreCustomMatches.next(isLoadingMore);

    return this.findCustomFixtureByTargetPaginatedGQL
      .fetch({
        ...(leagues?.length ? { leagues } : {}),
        ...(this.searchString ? { searchString: this.searchString } : {}),
        sports: this.sports,
        target: { pos: localStorage.getItem('posId') },
        pagination: { page: this.customPageIndex, limit: this.pageLimit },
      })
      .pipe(
        map(({ data }: any) => {
          const page = data.findCustomFixtureByTargetPaginated;
          const existing = reset ? [] : this.customMatches.getValue() || [];
          const enrichedPage = (page.objects || []).map((match: CustomFixtureType) => this.enrichCustomFixture(match));
          const merged = [...existing, ...enrichedPage];
          this.customPagination.next({
            page: this.customPageIndex,
            size: this.pageLimit,
            length: page.count,
          });
          this.customMatches.next(merged);
          this.isLast.next(!!page.isLast);
          this.loadingCustomMatches.next(false);
          this.loadingMoreCustomMatches.next(false);
          return merged;
        }),
      );
  }

  addUserToEventSubscription(fixture: string): Observable<EventSubscriptionType> {
    return this.addUserToEventSubscriptionGQL.mutate({ input: { fixture, user: localStorage.getItem('elevkusr') } }).pipe(
      map(({ data }: any) => {
        this.updateFixtureSubscriptionState(fixture, true);
        return data.addUserToEventSubscription;
      }),
    );
  }

  removeUserFromEventSubscription(fixture: string): Observable<any> {
    return this.removeUserFromEventSubscriptionGQL.mutate({ input: { fixture, user: localStorage.getItem('elevkusr') } }).pipe(
      map(({ data }: any) => {
        this.updateFixtureSubscriptionState(fixture, false);
        return data.removeUserFromEventSubscription;
      }),
    );
  }

  private updateSubscriptionInSubject(
    subject: BehaviorSubject<FixtureWithMatchExtras[]>,
    fixtureId: string,
    isSubscribed: boolean,
  ): void {
    if (!subject.value?.length) return;
    const index = subject.value.findIndex((match) => match.id === fixtureId);
    if (index === -1) return;
    const updated = [...subject.value];
    updated[index] = { ...updated[index], isUserSubscribedToFixtureEvents: isSubscribed };
    subject.next(updated);
  }
  
  private updateFixtureSubscriptionState(fixtureId: string, isSubscribed: boolean): void {
    this.updateSubscriptionInSubject(this.matchs, fixtureId, isSubscribed);
    this.updateSubscriptionInSubject(this.nearestMatches, fixtureId, isSubscribed);
  }

  getFixturesWithFilterWithSubs(filter?: FixtureFilterInput): Observable<FixtureWithMatchExtras[]> {
    const filterCriteria = {
      from: startOfDay(subMonths(new Date(), 3)),
      to: endOfDay(addWeeks(new Date(), 2)),
      team: [980],
    };
    this.loadingMatches.next(true);
    return this.getFixturesWithFilterWithSubsGQL
      .fetch({
        ...(filter ? { filter } : { filter: filterCriteria }),
        pagination: { page: 0, limit: 20000 },
      })
      .pipe(
        map(({ data }: any) => {
          this.loadingMatches.next(false);
          if (data.getFixturesWithFilterWithSubs.objects.length) {
            const matchs = orderBy(
              data.getFixturesWithFilterWithSubs.objects
                .filter((match: FixtureType) => match?.fixture?.date)
                .map((match: FixtureType) => this.enrichFixtureMatch(match)),
              [(match) => new Date(match.fixture!.date).getTime()],
              ['desc'],
            );
            this.matchs.next(matchs);
            return matchs;
          }
          this.matchs.next([]);
          return [];
        }),
      );
  }

  getNearestMatches(): Observable<FixtureWithMatchExtras[]> {
    const filter = {
      from: startOfDay(subMonths(new Date(), 3)),
      to: endOfDay(addWeeks(new Date(), 2)),
      /* from: addMinutes(new Date(), -150),
      to: addDays(new Date(), 3), */
      team: [980],
    };
    this.loadingMatches.next(true);
    return this.getFixturesWithFilterWithSubsGQL
      .fetch({
        filter,
        pagination: { page: 0, limit: 4 },
      })
      .pipe(
        map(({ data }: any) => {
          this.loadingMatches.next(false);
          if (data.getFixturesWithFilterWithSubs.objects.length) {
            const now = Date.now();
            const matchs = orderBy(
              data.getFixturesWithFilterWithSubs.objects
                .map((match: FixtureType) => this.enrichFixtureMatch(match))
                .map((match: FixtureType, index: number) => {
                  const enriched = this.enrichFixtureMatch(match);
                  // Override date: each match gets its own future date (1 week apart)
                  enriched.fixture.date = addWeeks(new Date(), index + 1).toISOString();
                  return enriched;
                }),
              /* .filter((match) => !match.finished && new Date(match.fixture!.date!).getTime() >= now) */
              [(match) => new Date(match.fixture!.date).getTime()],
              ['asc'],
            ).slice(0, 4);
            this.nearestMatches.next(matchs);
            return matchs;
          }
          this.nearestMatches.next([]);
          return [];
        }),
      );
  }

  findCustomTeamByTargetPaginated(): Observable<CustomTeamType[]> {
    return this.findCustomTeamByTargetPaginatedGQL
      .fetch({
        target: { pos: localStorage.getItem('posId') },
        pagination: { page: 0, limit: 20000 },
      })
      .pipe(
        map(({ data }: any) => {
          if (data.findCustomTeamByTargetPaginated.objects.length) {
            const teams = data.findCustomTeamByTargetPaginated.objects;
            this.customTeams.next(teams);
            return teams;
          }
          this.matchs.next([]);
          return [];
        }),
      );
  }

  findCustomLeagueByTargetPaginated(): Observable<CustomTeamType[]> {
    return this.findCustomLeagueByTargetPaginatedGQL
      .fetch({
        target: { pos: localStorage.getItem('posId') },
        pagination: { page: 0, limit: 20000 },
      })
      .pipe(
        map(({ data }: any) => {
          if (data.findCustomLeagueByTargetPaginated.objects.length) {
            const teams = data.findCustomLeagueByTargetPaginated.objects;
            this.customLeagues.next(teams);
            return teams;
          }
          this.customLeagues.next([]);
          return [];
        }),
      );
  }

  loadMoreCustomMatches(): Observable<CustomFixtureWithExtras[]> {
    if (this.isLast.getValue() || this.loadingCustomMatches.getValue() || this.loadingMoreCustomMatches.getValue()) {
      return of(this.customMatches.getValue() || []);
    }

    this.customPageIndex += 1;
    return this.findCustomFixtureByTargetPaginated(false);
  }

  getCurrentUserRank(input: LeaderboardWithRequiredCycleInput): Observable<CurrentUserRankType> {
    return this.GetCurrentUserRankGQL.fetch({ input: { ...input, target: { pos: localStorage.getItem('posId') } } }).pipe(
      map(({ data }: any) => {
        this.userRank.next(data.getCurrentUserRank);
        return data.GetCurrentUserRank;
      }),
    );
  }

  getCurrentUserPendingBetOrdersPagination(): Observable<BetOrderType[]> {
    this.loadingOrders.next(true);
    return this.getCurrentUserPendingBetOrdersPaginationGQL.fetch({ pagination: { page: this.pageIndex, limit: this.pageLimit } }).pipe(
      map(({ data }: any) => {
        const pendingBets = data.getCurrentUserPendingBetOrdersPagination.objects;
        this.loadingOrders.next(false);
        this.betOrders.next(pendingBets);
        this.isLast.next(data.getCurrentUserPendingBetOrdersPagination.isLast);
        return pendingBets;
      }),
    );
  }

  getCurrentUserFinishedBetOrdersPagination(): Observable<BetOrderType[]> {
    this.loadingOrders.next(true);
    return this.getCurrentUserFinishedBetOrdersPaginationGQL.fetch({ pagination: { page: this.pageIndex, limit: this.pageLimit } }).pipe(
      map(({ data }: any) => {
        const finishedBets = data.getCurrentUserFinishedBetOrdersPagination.objects;
        this.loadingOrders.next(false);
        this.isLast.next(data.getCurrentUserFinishedBetOrdersPagination.isLast);
        this.betOrders.next(finishedBets);
        return finishedBets;
      }),
    );
  }

  placeBet(input: BetOrderInput): Observable<BetOrderType[]> {
    return this.placeBetGQL.mutate({ input }).pipe(
      switchMap(({ data }: any) => {
        if (data) {
          return of(data.placeBet);
        }
        return of(null);
      }),
    );
  }

  getFixtureById(fixtureId: number): Observable<FixtureType> {
    return this.findOneByFixtureIdGQL.fetch({ fixtureId }).pipe(
      switchMap(({ data }: any) => {
        const match = data.findOneByFixtureId;
        const isLive = this.isLiveMatch(match);
        const fixture = {
          ...data.findOneByFixtureId,
          finished: this.finishedMatch(match),
          isLive,
        };
        return this.getPreMatchOddsFromProvider(fixture.fixture.id).pipe(
          map((res) => {
            fixture.odds = res?.[0]?.odds.length ? res?.[0]?.odds : null;
            fixture.soon = !isLive && !res?.[0]?.odds.length && !this.finishedMatch(fixture) && !fixture?.fixture.status?.elapsed;
            this.fixture.next(fixture);
            return fixture;
          }),
        );
      }),
    );
  }

  getTodayMatchs(filter?: FixtureFilterInput): Observable<FixtureWithOddsType[]> {
    const filterCriteria = {
      from: startOfDay(new Date()),
      to: endOfDay(new Date()),
    };
    this.loadingMatches.next(true);
    return this.getFixturesWithFilterAndOddsGQL
      .fetch({
        ...(filter ? { filter } : { filter: filterCriteria }),
        pagination: { page: 0, limit: 20000 },
      })
      .pipe(
        map(({ data }: any) => {
          this.loadingMatches.next(false);
          const todayMatches: FixtureWithOddsType[] = data.getFixturesWithFilterAndOdds.objects.map((match: FixtureWithOddsType) => {
            const isLive = this.isLiveMatch(match);
            return {
              ...match,
              finished: this.finishedMatch(match),
              isLive,
              soon: !isLive && !match.odds?.values?.length && !this.finishedMatch(match) && !match?.fixture.status?.elapsed,
            };
          });
          this.todayMatchs.next(todayMatches);
          return todayMatches;
        }),
      );
  }

  getFavoriteLeagueDefinitionsPagination(fetchPolicy: FetchPolicy = 'cache-first'): Observable<FavoriteLeagueDefinitionType[]> {
    return this.getFavoriteLeagueDefinitionsPaginationGQL
      .fetch(
        {
          pagination: { page: 0, limit: 20000 },
        },
        { fetchPolicy },
      )
      .pipe(
        map(({ data }: any) => {
          this.favouriteLeagues.next(data.getFavoriteLeagueDefinitionsPagination.objects);
          return data.getFavoriteLeagueDefinitionsPagination.objects;
        }),
      );
  }

  getPreMatchOddsFromProvider(fixtureId: number): Observable<OddsWithBetsType[]> {
    return this.getPreMatchOddsFromProviderGQL.fetch({ fixtureId }).pipe(
      map(({ data }: any) => {
        this.matchOdds.next(data.getPreMatchOddsFromProvider);
        return data.getPreMatchOddsFromProvider;
      }),
    );
  }

  isLiveMatch(match: FixtureWithOddsType | FixtureType): boolean {
    const now = new Date();
    const matchStart = new Date(match.fixture?.date);
    if (this.finishedMatch(match)) return false;
    // If match has started but not finished, it's live
    // Use 2h for regular, 3h for ET/PEN
    const matchEnd = addHours(matchStart, match?.fixture?.status?.short === 'ET' || match?.fixture?.status?.short === 'P' ? 3 : 2);
    return isAfter(now, matchStart) && isBefore(now, matchEnd);
  }

  finishedMatch(match: FixtureWithOddsType | FixtureType): boolean {
    const matchEndTime = addHours(
      new Date(match.fixture.date),
      match?.fixture?.status?.short === 'ET' || match?.fixture?.status?.short === 'P' ? 3 : 2,
    );
    return isAfter(new Date(), matchEndTime);
    /* return (
      isAfter(new Date(), matchEndTime) ||
      match?.fixture?.status?.short === 'FT' ||
      match?.fixture?.status?.long === 'Match Finished' ||
      match?.fixture?.status?.short === 'ABD'
    ); */
  }

  getUpComingMatchsWithOdds(filter: FixtureFilterInput): Observable<FixtureWithOddsType[]> {
    return this.getFixturesWithFilterAndOddsGQL
      .fetch({
        filter,
        pagination: { page: this.upcomingPageIndex, limit: this.upcomingPageLimit },
      })
      .pipe(
        map(({ data }: any) => {
          let mappedMatches: FixtureWithOddsType[] = [];
          mappedMatches = data.getFixturesWithFilterAndOdds.objects.map((match: any) => {
            const isLive = this.isLiveMatch(match);
            return {
              ...match,
              finished: this.finishedMatch(match),
              isLive,
              soon: !isLive && !match.odds?.values?.length && !this.finishedMatch(match) && !match?.fixture.status?.elapsed,
            };
          });
          mappedMatches = orderBy(mappedMatches, [(m) => (m.fixture?.date ? new Date(m.fixture.date).getTime() : 0)], ['asc']);
          this.upComingMatchs.next(mappedMatches);
          return mappedMatches;
        }),
      );
  }

  private enrichFixtureMatch(match: FixtureType): FixtureWithMatchExtras {
    const hasFullTimeScore = match?.score?.fulltime?.home != null || match?.score?.fulltime?.away != null;
    const hasScore = hasFullTimeScore || match?.goals?.home != null || match?.goals?.away != null;
    const isLive = this.isLiveMatch(match);
    const finished = this.finishedMatch(match);

    return {
      ...match,
      finished,
      isLive,
      hasScore,
      homeScore: match?.score?.fulltime?.home != null ? match.score.fulltime.home : (match?.goals?.home ?? 0),
      awayScore: match?.score?.fulltime?.away != null ? match.score.fulltime.away : (match?.goals?.away ?? 0),
    };
  }

  private enrichCustomFixture(match: CustomFixtureType): CustomFixtureWithExtras {
    const hasScore = match?.score?.home != null || match?.score?.away != null;
    const finished = hasScore && match?.score?.home != null && match?.score?.away != null;

    return {
      ...match,
      finished,
      isLive: false,
      hasScore,
      homeScore: match?.score?.home ?? '-',
      awayScore: match?.score?.away ?? '-',
    };
  }
}
